import React, { useState, useEffect } from 'react'
import {
  Button,
  Divider,
  Checkbox,
  Spin,
  Radio,
  List,
  message,
  Pagination,
  Input,
  Empty,
  Tag,
  Upload,
  Icon,
  Tabs,
  Breadcrumb
} from 'antd'
import {
  FileTextOutlined,
  SearchOutlined,
  CheckCircleFilled,
  UploadOutlined,
  LoadingOutlined,
  FolderOutlined,
  HomeOutlined
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { api } from '../../../services'
import { useDispatch, useSelector } from 'react-redux'
import actions from '../../../store/actions'
import { general } from '../../../constants'
import { bytesToSize } from '../../../utils'
import './ContentChoiceModal.scss'

const ACCEPTED_FILE_TYPES = {
  'application/pdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    '.docx',
  'application/vnd.ms-powerpoint': '.ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    '.pptx',
  'text/plain': '.txt'
}

const ContentChoiceStep = ({
  onCancel,
  onSelect,
  handleBack,
  handleFinalSubmit,
  isSubmitting
}) => {
  const { t } = useTranslation()
  const [mode, setMode] = useState('auto')
  const [activeTab, setActiveTab] = useState('myLibrary')
  const [isFetchingFiles, setIsFetchingFiles] = useState(false)
  const [isFetchingFolders, setIsFetchingFolders] = useState(false)
  const [files, setFiles] = useState([])
  const [myLibraryFiles, setMyLibraryFiles] = useState([])
  const [orgLibraryFiles, setOrgLibraryFiles] = useState([])
  const [selectedFiles, setSelectedFiles] = useState([])
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 })
  const [myLibraryPagination, setMyLibraryPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  })
  const [orgLibraryPagination, setOrgLibraryPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  })
  const [folderPagination, setFolderPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  })
  const [foldersByParentId, setFoldersByParentId] = useState({})
  const [filesByParentId, setFilesByParentId] = useState({})
  const [navigationTrigger, setNavigationTrigger] = useState(0)
  const [parentId, setParentId] = useState(null)

  // Upload-related states
  const [uploadFileList, setUploadFileList] = useState([])
  const [fileError, setFileError] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [fileUploadStatus, setFileUploadStatus] = useState({})

  const dispatch = useDispatch()
  const library = useSelector(state => state.library)
  const { uploadProgress, uploadSuccess } = library

  const handleSearchDebounced = _.debounce(value => {
    if (activeTab === 'myLibrary') {
      setMyLibraryPagination(prev => ({ ...prev, page: 1 }))
    } else {
      setOrgLibraryPagination(prev => ({ ...prev, page: 1 }))
    }
    setPagination(prev => ({ ...prev, page: 1 }))
    getAllFiles(value)
  }, 1000)

  useEffect(() => {
    if (mode === 'manual') {
      getAllFiles()
      getAllFolders()
    }
  }, [
    mode,
    myLibraryPagination.page,
    myLibraryPagination.limit,
    orgLibraryPagination.page,
    orgLibraryPagination.limit,
    activeTab,
    navigationTrigger
  ])

  useEffect(() => {
    setSelectedFiles([])
    setUploadFileList([])
    setFileError('')
    setActiveTab('myLibrary')
    setPagination({ page: 1, limit: 10, total: 0 })
    setMyLibraryPagination({ page: 1, limit: 10, total: 0 })
    setOrgLibraryPagination({ page: 1, limit: 10, total: 0 })
    setFolderPagination({ page: 1, limit: 10, total: 0 })
    setMyLibraryFiles([])
    setOrgLibraryFiles([])
    setFoldersByParentId({})
    setFilesByParentId({})
    setParentId(null)
  }, [mode])

  useEffect(() => {
    checkFileError()
  }, [uploadFileList])

  const checkFileError = () => {
    if (mode === 'upload') {
      if (uploadFileList.length === 0) {
        setFileError(t('v5:please_upload_at_least_one_supported_file'))
      } else if (uploadFileList.length > 5) {
        setFileError(t('v5:you_can_upload_a_maximum_of_files'))
      } else {
        setFileError('')
      }
    }
  }

  const handleFileChange = info => {
    let newFileList = [...info.fileList]

    // Filter files to only accept supported file types
    newFileList = newFileList.filter(file => {
      const isAcceptedType = Object.keys(ACCEPTED_FILE_TYPES).includes(
        file.type
      )

      if (!isAcceptedType) {
        const acceptedExtensions = Object.values(ACCEPTED_FILE_TYPES).join(', ')
        message.error(
          `${file.name} ${t('v5:file_not_supported')} ${acceptedExtensions}`
        )
        return false
      }
      return true
    })

    setUploadFileList(newFileList)
  }

  // Helper functions for folder management
  const getCustomPathKey = parentId => {
    return parentId ? `folder_${parentId}` : 'root'
  }

  const updateFolderState = (parentId, rows, count) => {
    const pathKey = getCustomPathKey(parentId)
    setFoldersByParentId(prev => ({ ...prev, [pathKey]: rows }))
    setFolderPagination(prev => ({ ...prev, total: count }))
  }

  const updateFileState = (parentId, rows, count) => {
    const pathKey = getCustomPathKey(parentId)
    setFilesByParentId(prev => ({ ...prev, [pathKey]: rows }))
    setPagination(prev => ({ ...prev, total: count }))
  }

  const findFolderById = id => {
    if (!id) return null

    // Search in folders
    for (const pathKey in foldersByParentId) {
      const items = foldersByParentId[pathKey] || []
      const found = items.find(
        item => item.id === id && item.media_format === 'FOLDER'
      )
      if (found) return found
    }

    // Search in files as fallback
    for (const pathKey in filesByParentId) {
      const items = filesByParentId[pathKey] || []
      const found = items.find(
        item => item.id === id && item.media_format === 'FOLDER'
      )
      if (found) return found
    }

    return null
  }

  const getBreadcrumbFolders = () => {
    const crumbs = []
    if (!parentId) return crumbs

    let folderId = parentId
    const breadcrumbChain = []

    while (folderId) {
      const folder = findFolderById(folderId)
      if (folder) {
        breadcrumbChain.unshift(folder)
        folderId = folder.parent_id
      } else {
        break
      }
    }

    return breadcrumbChain
  }

  const navigateToFolder = folder => {
    if (typeof folder === 'object' && folder.media_format === 'FOLDER') {
      setParentId(folder.id)
      setPagination(prev => ({ ...prev, page: 1 }))
      setFolderPagination(prev => ({ ...prev, page: 1 }))
      setNavigationTrigger(prev => prev + 1)
    } else if (typeof folder === 'string' && folder === '/') {
      setParentId(null)
      setPagination(prev => ({ ...prev, page: 1 }))
      setFolderPagination(prev => ({ ...prev, page: 1 }))
      setNavigationTrigger(prev => prev + 1)
    }
  }

  const getCurrentDirectoryItems = () => {
    const pathKey = getCustomPathKey(parentId)
    const folders = foldersByParentId[pathKey] || []
    const files = filesByParentId[pathKey] || []
    return [...folders, ...files]
  }

  const getAllFiles = async (searchValue = '', currentParentId = parentId) => {
    setIsFetchingFiles(true)
    try {
      const currentPagination =
        activeTab === 'myLibrary' ? myLibraryPagination : orgLibraryPagination
      const offset = (currentPagination.page - 1) * currentPagination.limit
      const response = await api.library.getLibraryList({
        limit: currentPagination.limit,
        onlyMedia: true,
        offset,
        search: searchValue,
        sort: 'NEWEST',
        type: 'PDF,DOC,PPT,TEXT',
        parent_id: currentParentId,
        is_ai_enabled: true,

        ...(activeTab === 'orgLibrary' && { isOrg: true }),
        ...(activeTab === 'myLibrary' && { isActiveOrg: true })
      })
      const rows = response.data.rows || []
      const count = response.data.count || 0

      updateFileState(currentParentId, rows, count)

      if (activeTab === 'myLibrary') {
        setMyLibraryFiles(rows)
        setMyLibraryPagination(prev => ({ ...prev, total: count }))
      } else {
        setOrgLibraryFiles(rows)
        setOrgLibraryPagination(prev => ({ ...prev, total: count }))
      }

      setFiles(rows) // Keep for backward compatibility
      setPagination(prev => ({ ...prev, total: count }))
    } catch (error) {
      console.error('Error fetching files:', { error })
      if (
        error.response &&
        error.response.data &&
        error.response.data.errorType === 'AccessDenied'
      ) {
        message.error(
          'Access denied: Service configuration issue. Please contact your administrator.'
        )
      } else if (error.response && error.response.status === 403) {
        message.error(
          'Access denied: You do not have permission to access this resource.'
        )
      } else if (error.response && error.response.status === 401) {
        message.error('Authentication failed. Please log in again.')
      } else {
        message.error('Failed to load documents. Please try again.')
      }
    } finally {
      setIsFetchingFiles(false)
    }
  }

  const getAllFolders = async (currentParentId = parentId) => {
    setIsFetchingFolders(true)
    try {
      const offset = (folderPagination.page - 1) * folderPagination.limit
      const response = await api.library.getSopList({
        limit: folderPagination.limit,
        offset,
        onlyFolder: true,
        parent_id: currentParentId,
        sort: 'NEWEST',

        ...(activeTab === 'orgLibrary' && { isOrg: true }),
        ...(activeTab === 'myLibrary' && { isActiveOrg: true })
      })

      const rows = response.data.rows || []
      const count = response.data.count || 0

      updateFolderState(currentParentId, rows, count)
    } catch (error) {
      console.error('Error fetching folders:', { error })
      if (
        error.response &&
        error.response.data &&
        error.response.data.errorType === 'AccessDenied'
      ) {
        message.error(
          'Access denied: Service configuration issue. Please contact your administrator.'
        )
      } else if (error.response && error.response.status === 403) {
        message.error(
          'Access denied: You do not have permission to access this resource.'
        )
      } else if (error.response && error.response.status === 401) {
        message.error('Authentication failed. Please log in again.')
      } else {
        message.error('Failed to load folders. Please try again.')
      }
    } finally {
      setIsFetchingFolders(false)
    }
  }


  const handleFileSelect = checkedValues => {
    const currentSelectedIds = selectedFiles.map(f => f.id)
    const newlySelected = files.filter(
      file =>
        checkedValues.includes(file.id) && !currentSelectedIds.includes(file.id)
    )
    const deselectedIds = files
      .filter(file => !checkedValues.includes(file.id))
      .map(file => file.id)
    const updatedSelected = [
      ...selectedFiles.filter(f => !deselectedIds.includes(f.id)),
      ...newlySelected
    ]
    setSelectedFiles(updatedSelected)
  }

  const handleRemoveSelected = fileId => {
    setSelectedFiles(selectedFiles.filter(f => f.id !== fileId))
  }

  const getSelectedIds = () => {
    return selectedFiles.map(f => f.id)
  }

  const handleOk = () => {
    if (mode === 'manual') {
      if (selectedFiles.length === 0) {
        message.warning('Please select at least one document')
        return
      }
      const selectedIds = selectedFiles.map(f => f.path)
      onSelect && onSelect(selectedIds)
    } else if (mode === 'upload') {
      if (uploadFileList.length === 0) {
        message.warning('Please upload at least one file')
        return
      }
      if (fileError) {
        message.error(fileError)
        return
      }
      // Return the file list for upload processing
      onSelect && onSelect(uploadFileList)
    } else {
      onSelect && onSelect([])
    }
    onCancel && onCancel()
  }

  const handleCancel = () => {
    setMode('auto')
    setSelectedFiles([])
    setUploadFileList([])
    setFileError('')
    onCancel && onCancel()
  }

  const getItemTypeTag = mimeType => {
    const type = mimeType.split('/')[0]
    const subType = mimeType.split('/')[1]

    if (type === 'application') {
      if (subType === 'pdf') return { label: 'PDF', color: '#ff4757' }
      if (subType.includes('word') || subType === 'msword')
        return { label: 'Word', color: '#2b7de9' }
      if (subType.includes('powerpoint') || subType.includes('presentation'))
        return { label: 'PowerPoint', color: '#ff6b35' }
      if (subType.includes('excel') || subType.includes('spreadsheet'))
        return { label: 'Excel', color: '#1e7145' }
      if (subType === 'json') return { label: 'JSON', color: '#ffa502' }
      return { label: 'Document', color: '#8c8c8c' }
    } else if (type === 'text') {
      if (subType === 'plain') return { label: 'Text', color: '#5f27cd' }
      if (subType === 'csv') return { label: 'CSV', color: '#00d2d3' }
      if (subType === 'html') return { label: 'HTML', color: '#ff6348' }
      return { label: 'Text', color: '#5f27cd' }
    }
    return { label: mimeType, color: '#8c8c8c' }
  }

  const checkedCompletUpload = item => {
    const filterSuccessFiles = _.filter(
      uploadSuccess,
      ele => ele.id === item.id
    )
    return !(uploadSuccess.length && filterSuccessFiles.length)
  }

  const uploadFiles = async files => {
    setUploadedFiles([])
    setFileUploadStatus({})
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      try {
        setFileUploadStatus(prevStatus => ({
          ...prevStatus,
          [file.name]: 'uploading'
        }))
        const res = await dispatch(
          actions.library.upload(file.originFileObj, {
            ai_language: 'en-US',
            isAiEnabled: true
          })
        )
        const newFiles = [...uploadedFiles, res]
        setUploadedFiles(newFiles)
        setFileUploadStatus(prevStatus => ({
          ...prevStatus,
          [file.name]: 'success'
        }))
        return newFiles
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error)
        message.error(`Failed to upload ${file.name}`)
        setFileUploadStatus(prevStatus => ({
          ...prevStatus,
          [file.name]: 'error'
        }))
        throw error
      }
    }
  }

  const handleConfirmation = async () => {
    if (mode === 'manual') {
      if (selectedFiles.length === 0) {
        message.warning('Please select at least one document')
        return
      }
      if (handleFinalSubmit) handleFinalSubmit(selectedFiles)
      return
    }

    if (mode === 'upload') {
      if (uploadFileList.length === 0) {
        message.warning('Please upload at least one file')
        return
      }
      if (fileError) {
        message.error(fileError)
        return
      }

      try {
        setIsUploading(true)
        const newFiles = await uploadFiles(uploadFileList)
        setIsUploading(false)
        if (handleFinalSubmit) handleFinalSubmit(newFiles)
      } catch (e) {
        setIsUploading(false)
      }
      return
    }

    if (handleFinalSubmit) handleFinalSubmit([])
  }

  return (
    <div className="content-choice-modal">
      <div className="content-choice-modal__header">
        {t('v4:course_content_source')}
      </div>
      <div style={{ marginBottom: 28 }}>
        <div className="content-choice-modal__description">
          {t('v4:choose_how_to_source')}
        </div>
        <Radio.Group
          value={mode}
          onChange={e => setMode(e.target.value)}
          className="content-choice-modal__radio-group"
        >
          <div
            className={`content-choice-modal__radio-option content-choice-modal__radio-option--auto ${mode === 'auto'
              ? 'content-choice-modal__radio-option--active'
              : ''
              }`}
            onClick={() => setMode('auto')}
          >
            <Radio value="auto">
              <div className="content-choice-modal__radio-content">
                <div className="content-choice-modal__radio-content-title">
                  {t('v4:automatic_selection')}
                </div>
                <div className="content-choice-modal__radio-content-description">
                  {t('v4:system_will_automatically_select_relavant_documents')}
                </div>
              </div>
            </Radio>
          </div>
          <div
            className={`content-choice-modal__radio-option content-choice-modal__radio-option--manual ${mode === 'manual'
              ? 'content-choice-modal__radio-option--active'
              : ''
              }`}
            onClick={() => setMode('manual')}
          >
            <Radio value="manual">
              <div className="content-choice-modal__radio-content">
                <div className="content-choice-modal__radio-content-title">
                  {t('v4:manual_selection')}
                </div>
                <div className="content-choice-modal__radio-content-description">
                  {t('v4:choose_specific_documents_from_your_library')}
                </div>
              </div>
            </Radio>
          </div>
          <div
            className={`content-choice-modal__radio-option content-choice-modal__radio-option--upload ${mode === 'upload'
              ? 'content-choice-modal__radio-option--active'
              : ''
              }`}
            onClick={() => setMode('upload')}
          >
            <Radio value="upload">
              <div className="content-choice-modal__radio-content">
                <div className="content-choice-modal__radio-content-title">
                  {t('v4:upload_files')}
                </div>
                <div className="content-choice-modal__radio-content-description">
                  {t('v4:upload_new_documents')}
                </div>
              </div>
            </Radio>
          </div>
        </Radio.Group>
      </div>

      {mode === 'upload' && (
        <div className="content-choice-modal__upload-section">
          <Divider />
          <div>
            <div style={{ marginBottom: 20 }}>
              <Upload
                multiple
                accept=".pdf,.doc,.docx,.ppt,.pptx,.txt"
                fileList={uploadFileList}
                onChange={handleFileChange}
                beforeUpload={() => false}
              >
                <Button
                  icon="upload"
                  size="large"
                  className="content-choice-modal__upload-button"
                >
                  {t('v5:select_files_to_upload')}
                </Button>
              </Upload>
              {fileError && (
                <div className="content-choice-modal__upload-error">
                  {fileError}
                </div>
              )}
            </div>

            {uploadFileList.length > 0 && (
              <div className="content-choice-modal__upload-preview">
                <div className="content-choice-modal__upload-header">
                  <div className="content-choice-modal__upload-header-info">
                    <CheckCircleFilled />
                    <strong>
                      {uploadFileList.length} {t('v4:file')}
                      {uploadFileList.length > 1 ? 's' : ''}{' '}
                      {t('v4:ready_to_upload')}
                    </strong>
                  </div>
                  <Button
                    size="small"
                    onClick={() => setUploadFileList([])}
                    className="content-choice-modal__upload-header-clear"
                  >
                    {t('v4:clear_all')}
                  </Button>
                </div>
                <div className="content-choice-modal__upload-list">
                  {uploadFileList.map((file, index) => (
                    <div
                      key={file.uid}
                      className="content-choice-modal__upload-item"
                    >
                      {file.isUploading ? (
                        <LoadingOutlined className="content-choice-modal__upload-item-icon" />
                      ) : file.isUploaded ? (
                        <CheckCircleFilled className="content-choice-modal__upload-item-icon" />
                      ) : (
                        <FileTextOutlined className="content-choice-modal__upload-item-icon" />
                      )}
                      <div className="content-choice-modal__upload-item-content">
                        <div className="content-choice-modal__upload-item-content-name">
                          {file.name}
                        </div>
                        <div className="content-choice-modal__upload-item-content-size">
                          {(file.size / 1024).toFixed(2)} KB
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="content-choice-modal__upload-progress">
                  {uploadProgress.length > 0 &&
                    uploadSuccess.length !== uploadProgress.length &&
                    uploadProgress.map((item, ind) =>
                      item.percent < 100 ? (
                        <div key={item.uid} className="upload-modal__item">
                          <div className="upload-modal-item">
                            <div className="upload-modal-item__percent-border">
                              <div
                                className={`upload-modal-item__percent ${item.status === general.CANCELED
                                  ? 'upload-modal-item__percent__cancel'
                                  : ''
                                  }`}
                                style={{ width: item.percent + '%' }}
                              />
                            </div>
                          </div>

                          {item.status !== general.CANCELED && (
                            <div className="upload-modal-item__weight">
                              {checkedCompletUpload(item) && (
                                <span>
                                  {bytesToSize(item.loaded)} of{' '}
                                  {bytesToSize(item.total)}
                                </span>
                              )}
                              {!checkedCompletUpload(item) && (
                                <span className="upload-modal-item__weight__complete">
                                  {uploadedFiles[ind] &&
                                    uploadedFiles[ind].removed
                                    ? 'Removed'
                                    : 'Completed'}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : null
                    )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {mode === 'manual' && (
        <div className="content-choice-modal__manual-section">
          <Divider />
          <div>
            <div style={{ marginBottom: 20 }}>
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                className="content-choice-modal__tabs"
              >
                <Tabs.TabPane tab={t('v5:my_library')} key="myLibrary" />
                <Tabs.TabPane
                  tab={t('media:organization_library')}
                  key="orgLibrary"
                />
              </Tabs>
              <Input.Search
                placeholder={t('v5:search_documents_by_name')}
                allowClear
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                disabled={isFetchingFiles}
                onChange={e => {
                  const value = e.target.value
                  handleSearchDebounced(value)
                }}
                onSearch={v => {
                  handleSearchDebounced(v)
                }}
                className="content-choice-modal__search"
                size="large"
              />
            </div>

            {isFetchingFiles || isFetchingFolders ? (
              <div className="content-choice-modal__loading">
                <Spin size="large" />
                <div className="content-choice-modal__loading-text">
                  {t('v5:loading_documents')}
                </div>
              </div>
            ) : (
              <>
                {/* Breadcrumb Navigation */}
                {parentId && (
                  <div className="content-choice-modal__breadcrumb" style={{ marginBottom: 16 }}>
                    <Breadcrumb>
                      <Breadcrumb.Item>
                        <a
                          onClick={() => navigateToFolder('/')}
                          style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <HomeOutlined />
                          {t('v4:home')}
                        </a>
                      </Breadcrumb.Item>
                      {getBreadcrumbFolders().map((folder) => (
                        <Breadcrumb.Item key={folder.id}>
                          <a onClick={() => navigateToFolder(folder)}>
                            {folder.name || folder.title || `Folder ${folder.id}`}
                          </a>
                        </Breadcrumb.Item>
                      ))}
                    </Breadcrumb>
                  </div>
                )}

                {selectedFiles.length > 0 && (
                  <div className="content-choice-modal__selected-files">
                    <div className="content-choice-modal__selected-files-container">
                      <div className="content-choice-modal__selected-files-header">
                        <div className="content-choice-modal__selected-files-header-info">
                          <CheckCircleFilled />
                          <strong>
                            {selectedFiles.length} {t('v5:document')}
                            {selectedFiles.length > 1 ? 's' : ''}{' '}
                            {t('v5:selected')}
                          </strong>
                        </div>
                        <Button
                          size="small"
                          onClick={() => setSelectedFiles([])}
                          className="content-choice-modal__selected-files-header-clear"
                        >
                          {t('v4:clear_all')}
                        </Button>
                      </div>
                      <div className="content-choice-modal__selected-files-tags">
                        {selectedFiles.map(file => (
                          <Tag
                            key={file.id}
                            closable
                            onClose={() => handleRemoveSelected(file.id)}
                            title={file.title || file.filename || file.name}
                          >
                            <span>
                              {file.title || file.filename || file.name}
                            </span>
                          </Tag>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="content-choice-modal__document-list">
                  {getCurrentDirectoryItems().length === 0 ? (
                    <Empty
                      description={<span>{t('No documents found')}</span>}
                    />
                  ) : (
                    <Checkbox.Group
                      style={{ width: '100%' }}
                      value={getSelectedIds()}
                      onChange={handleFileSelect}
                    >
                      <List
                        dataSource={getCurrentDirectoryItems()}
                        renderItem={(item, index) => {
                          const isFolder = item.media_format === 'FOLDER'
                          const typeInfo = getItemTypeTag(
                            item.media_mime_type || ''
                          )
                          const isSelected = getSelectedIds().includes(item.id)
                          return (
                            <List.Item
                              className={`document-list-item-enhanced ${isSelected ? 'document-list-item-selected' : ''
                                } ${isFolder ? 'document-list-item-folder' : ''}`}
                              onClick={() => {
                                if (isFolder) {
                                  navigateToFolder(item)
                                }
                              }}
                              style={isFolder ? { cursor: 'pointer' } : {}}
                            >
                              {isFolder ? (
                                <div className="document-item" style={{ flex: 1 }}>
                                  <div className="document-item__icon document-item__icon--folder">
                                    <FolderOutlined />
                                  </div>
                                  <div className="document-item__content">
                                    <div className="document-item__content-title">
                                      {item.title || item.name || item.filename}
                                    </div>
                                    <Tag className="document-item__content-tag document-item__content-tag--folder">
                                      Folder
                                    </Tag>
                                  </div>
                                </div>
                              ) : (
                                <Checkbox value={item.id} style={{ flex: 1 }}>
                                  <div className="document-item">
                                    <div
                                      className={`document-item__icon document-item__icon--${typeInfo.label.toLowerCase()}`}
                                    >
                                      <FileTextOutlined />
                                    </div>
                                    <div className="document-item__content">
                                      <div className="document-item__content-title">
                                        {item.title || item.filename || item.name}
                                      </div>
                                      {item.media_mime_type && (
                                        <Tag
                                          className={`document-item__content-tag document-item__content-tag--${typeInfo.label.toLowerCase()}`}
                                        >
                                          {typeInfo.label}
                                        </Tag>
                                      )}
                                    </div>
                                  </div>
                                </Checkbox>
                              )}
                            </List.Item>
                          )
                        }}
                      />
                    </Checkbox.Group>
                  )}
                </div>

                {files.length > 0 && (
                  <div className="content-choice-modal__pagination">
                    <div className="content-choice-modal__pagination-info">
                      {t('v5:showing')}{' '}
                      {((activeTab === 'myLibrary'
                        ? myLibraryPagination
                        : orgLibraryPagination
                      ).page -
                        1) *
                        (activeTab === 'myLibrary'
                          ? myLibraryPagination
                          : orgLibraryPagination
                        ).limit +
                        1}
                      -
                      {Math.min(
                        (activeTab === 'myLibrary'
                          ? myLibraryPagination
                          : orgLibraryPagination
                        ).page *
                        (activeTab === 'myLibrary'
                          ? myLibraryPagination
                          : orgLibraryPagination
                        ).limit,
                        (activeTab === 'myLibrary'
                          ? myLibraryPagination
                          : orgLibraryPagination
                        ).total
                      )}{' '}
                      of{' '}
                      {
                        (activeTab === 'myLibrary'
                          ? myLibraryPagination
                          : orgLibraryPagination
                        ).total
                      }
                    </div>
                    <Pagination
                      current={
                        (activeTab === 'myLibrary'
                          ? myLibraryPagination
                          : orgLibraryPagination
                        ).page
                      }
                      pageSize={
                        (activeTab === 'myLibrary'
                          ? myLibraryPagination
                          : orgLibraryPagination
                        ).limit
                      }
                      total={
                        (activeTab === 'myLibrary'
                          ? myLibraryPagination
                          : orgLibraryPagination
                        ).total
                      }
                      showSizeChanger
                      pageSizeOptions={['5', '10', '20', '50']}
                      onChange={(page, pageSize) => {
                        if (activeTab === 'myLibrary') {
                          setMyLibraryPagination(prev => ({
                            ...prev,
                            page,
                            limit: pageSize
                          }))
                        } else {
                          setOrgLibraryPagination(prev => ({
                            ...prev,
                            page,
                            limit: pageSize
                          }))
                        }
                        setPagination(prev => ({
                          ...prev,
                          page,
                          limit: pageSize
                        }))
                      }}
                      onShowSizeChange={(current, size) => {
                        if (activeTab === 'myLibrary') {
                          setMyLibraryPagination(prev => ({
                            ...prev,
                            page: 1,
                            limit: size
                          }))
                        } else {
                          setOrgLibraryPagination(prev => ({
                            ...prev,
                            page: 1,
                            limit: size
                          }))
                        }
                        setPagination(prev => ({
                          ...prev,
                          page: 1,
                          limit: size
                        }))
                      }}
                      size="small"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div className="content-choice-modal__footer">
        <Divider />
        <div className="content-choice-modal__footer-actions">
          <Button onClick={handleBack} size="large">
            {t('buttons:back')}
          </Button>
          <Button
            type="primary"
            onClick={handleConfirmation}
            size="large"
            loading={isSubmitting || isUploading}
            disabled={
              isSubmitting ||
              isUploading ||
              (mode === 'manual' && selectedFiles.length === 0) ||
              (mode === 'upload' &&
                (uploadFileList.length === 0 || !!fileError))
            }
          >
            {t('v2:button_submit')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ContentChoiceStep
